import { useCallback, useEffect, useRef } from 'react';

import type { ApolloQueryResult, OperationVariables } from '@apollo/client';

export type UseSequentialPollingInput<T> = {
  query: () => Promise<ApolloQueryResult<T>>;
  pollInterval?: number;
  queryVariables?: OperationVariables;
};

export type UseSequentialPollingOutput = {
  startPolling: () => Promise<void>;
  stopPolling: () => void;
};

export type UseSequentialPollingHook<T> = (input: UseSequentialPollingInput<T>) => UseSequentialPollingOutput;

export default function useSequentialPolling<T>({
  query,
  pollInterval,
  queryVariables,
}: UseSequentialPollingInput<T>): UseSequentialPollingOutput {
  const pollingTimeoutId = useRef<number>();

  const poll = useCallback(
    async (skipFirstCall?: boolean) => {
      if (!skipFirstCall) {
        try {
          await query();
        } catch (err) {
          // just consume the error
        }
      }

      pollingTimeoutId.current = setTimeout(poll, pollInterval);
    },
    [query, pollInterval]
  );

  const stopPolling = useCallback(() => {
    if (pollingTimeoutId.current) {
      clearTimeout(pollingTimeoutId.current);
      pollingTimeoutId.current = undefined;
    }
  }, []);

  const startPolling = useCallback(async () => {
    stopPolling();
    await poll();
  }, [stopPolling, poll]);

  useEffect(() => {
    if (queryVariables && pollInterval) {
      poll(true);
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [startPolling, stopPolling, poll, queryVariables, pollInterval]);

  return { startPolling, stopPolling };
}
